import { useState } from "react";

import { DateTime } from "luxon";

import { Button } from "@components/Button";
import { DateRangePicker } from "@components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/Dialog";
import { Stack } from "@components/Stack";
import { Typography } from "@components/Typography";

interface CustomDatesDialogProps {
	onClose: () => void;
	onSelect: (dateFrom: string, dateTo: string, label: string) => void;
}

export const CustomDatesDialog = ({ onClose, onSelect }: CustomDatesDialogProps) => {
	const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
	const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
	const [isErrorDate, setIsErrorDate] = useState(false);

	const handleRangeChange = (range: { from: Date; to: Date }) => {
		setDateFrom(range.from);
		setDateTo(range.to);
		validateRange(range.from, range.to);
	};

	const validateRange = (from: Date | undefined, to: Date | undefined) => {
		if (from && to) {
			const diff = DateTime.fromJSDate(to).diff(DateTime.fromJSDate(from), "years").years;
			setIsErrorDate(diff > 1);
		} else {
			setIsErrorDate(false);
		}
	};

	const handleSelect = () => {
		if (isErrorDate || !dateFrom || !dateTo) return;

		const rawFrom = new Date(dateFrom);
		const rawTo = new Date(dateTo);
		rawFrom.setHours(0, 0, 0);
		rawTo.setHours(23, 59, 59);

		const formattedFrom = DateTime.fromJSDate(rawFrom).toFormat("dd-MMM-yyyy HH:mm:ss");
		const formattedTo = DateTime.fromJSDate(rawTo).toFormat("dd-MMM-yyyy HH:mm:ss");
		const label = `${DateTime.fromJSDate(rawFrom).toFormat("dd MMM")} - ${DateTime.fromJSDate(rawTo).toFormat("dd MMM")}`;

		onSelect(formattedFrom, formattedTo, label);
		onClose();
	};

	return (
		<Dialog open onOpenChange={onClose}>
			<DialogContent className="max-w-90">
				<DialogHeader>
					<DialogTitle>Custom dates</DialogTitle>
				</DialogHeader>

				<Stack spacing={12}>
					<DateRangePicker
						value={
							dateFrom && dateTo
								? {
										from: dateFrom,
										to: dateTo,
									}
								: undefined
						}
						onChange={handleRangeChange}
						startPlaceholder="Start"
						endPlaceholder="End"
						triggerClassName="w-full"
					/>

					{isErrorDate ? (
						<Typography color="error" size="sm">
							Difference between dateFrom and dateTo must not be more than a year.
						</Typography>
					) : null}
				</Stack>

				<Stack direction="row" spacing={8} wide className="mt-5 mb-6">
					<Button type="secondary" onClick={onClose} wide>
						<Typography weight="bold" size="sm">
							Cancel
						</Typography>
					</Button>
					<Button onClick={handleSelect} disable={isErrorDate || !dateFrom || !dateTo} wide>
						<Typography weight="bold" size="sm">
							Save
						</Typography>
					</Button>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
